import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, Center, useToast,
  Button, HStack, useColorModeValue, Badge, Text, Flex, Tag, Icon,
  InputGroup, InputLeftElement, Input, Alert, AlertIcon, AlertTitle, AlertDescription,
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, TimeIcon, SearchIcon, QuestionIcon } from '@chakra-ui/icons';
import { FiEye, FiMessageCircle, FiAlertCircle, FiFilter, FiRefreshCw, FiClock, FiCheck } from 'react-icons/fi';
import AdminChatModal from '../AdminChat/AdminChatModal';
import WorkerHelpDetailsModal from './components/WorkerHelpDetailsModal';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const WorkerNeedHelp = () => {
  const [helpRequests, setHelpRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedHelpRequest, setSelectedHelpRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const toast = useToast();

  // Color scheme
  const tableBorderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const filterBg = useColorModeValue('white', 'gray.800');
  const filterBorderColor = useColorModeValue('gray.200', 'gray.600');

  // Fetch help requests on component mount
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchHelpRequests();
  }, []);

  // Filter help requests when data, search term, or status filter changes
  useEffect(() => {
    applyFilters();
  }, [helpRequests, searchTerm, statusFilter]);

  // Function to apply filters
  const applyFilters = () => {
    let result = [...helpRequests];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(item => 
        (item.userName && item.userName.toLowerCase().includes(searchLower)) || 
        (item.reason && item.reason.toLowerCase().includes(searchLower)) ||
        (item.additionalNotes && item.additionalNotes.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'active':
          result = result.filter(item => !item.actionTaken); // All non-completed
          break;
        case 'pending':
          result = result.filter(item => !item.inProgress && !item.actionTaken);
          break;
        case 'inProgress':
          result = result.filter(item => item.inProgress && !item.actionTaken && !item.pendingConfirmation);
          break;
        case 'pendingConfirmation':
          result = result.filter(item => item.pendingConfirmation && !item.actionTaken);
          break;
        case 'completed':
          result = result.filter(item => item.actionTaken);
          break;
        default:
          break;
      }
    }
    
    setFilteredRequests(result);
  };

  // Function to fetch help requests list
  const fetchHelpRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/workerHelp/getallworkerhelp`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch help requests');
      }

      const data = await response.json();
      setHelpRequests(data.data || []);
      setFilteredRequests(data.data || []);
    } catch (error) {
      setError(error.message);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar solicitações de ajuda',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('active');
  };

  // Fetch help request details by ID
  const fetchHelpRequestById = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/workerHelp/getworkerhelp/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch help request details');
      }

      const data = await response.json();

      // Auto-change to "Em Atendimento" if still in "Aguardando"
      if (data.success && data.data && !data.data.inProgress && !data.data.actionTaken) {
        // Call the PUT endpoint to update status
        await fetch(
          `${API_BASE_URL}api/workerHelp/confirmActionTaken/${id}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ inProgress: true }),
          }
        );

        // Update local state
        const updatedData = { ...data.data, inProgress: true };
        setSelectedHelpRequest(updatedData);

        // Update the list as well
        const updatedRequests = helpRequests.map((hr) =>
          hr._id === id ? { ...hr, inProgress: true } : hr
        );
        setHelpRequests(updatedRequests);
      } else {
        setSelectedHelpRequest(data.data);
      }

      setIsModalOpen(true);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao carregar detalhes da solicitação de ajuda',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle open chat modal
  const openAdminChat = (userId) => {
    setCurrentUserId(userId);
    setIsChatModalOpen(true);
  };

  // Update status (Action Taken)
  const handleActionTaken = async (helpRequestId) => {
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');

      // Send request to update backend
      const response = await fetch(
        `${API_BASE_URL}api/workerHelp/confirmActionTaken/${helpRequestId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        // USE BACKEND RESPONSE DATA instead of assuming values
        const updatedFields = {
          inProgress: result.data.inProgress,
          actionTaken: result.data.actionTaken,
          pendingConfirmation: result.data.pendingConfirmation
        };

        // Update the local state with actual backend data
        const updatedRequests = helpRequests.map((hr) =>
          hr._id === helpRequestId ? { ...hr, ...updatedFields } : hr
        );
        setHelpRequests(updatedRequests);

        // Also update selectedHelpRequest if it's the one being updated
        if (selectedHelpRequest && selectedHelpRequest._id === helpRequestId) {
          setSelectedHelpRequest({
            ...selectedHelpRequest,
            ...updatedFields
          });
        }

        // Show appropriate message based on state
        if (result.pendingConfirmation) {
          toast({
            title: 'Aguardando Confirmação',
            description: 'Mensagem de confirmação enviada ao trabalhador',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Sucesso',
            description: result.message || 'Status atualizado com sucesso',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'Info',
          description: result.message || 'Não foi possível atualizar',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao atualizar status',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Status Badge Component
   */
  const StatusBadge = ({ isCompleted, isInProgress, isPendingConfirmation }) => {
    let colorScheme, status, icon;

    if (isCompleted) {
      colorScheme = 'green';
      status = 'Concluído';
      icon = CheckIcon;
    } else if (isPendingConfirmation) {
      colorScheme = 'orange';
      status = 'Aguardando Confirmação';
      icon = QuestionIcon;
    } else if (isInProgress) {
      colorScheme = 'yellow';
      status = 'Em Andamento';
      icon = TimeIcon;
    } else {
      colorScheme = 'red';
      status = 'Aguardando';
      icon = WarningIcon;
    }

    return (
      <Badge colorScheme={colorScheme} px={2} py={1} borderRadius="full">
        <Flex align="center">
          <Icon as={icon} mr={1} boxSize="10px" />
          <Text>{status}</Text>
        </Flex>
      </Badge>
    );
  };

  // Count help requests by status
  const getCounts = () => {
    const pending = helpRequests.filter(item => !item.inProgress && !item.actionTaken).length;
    const inProgress = helpRequests.filter(item => item.inProgress && !item.actionTaken && !item.pendingConfirmation).length;
    const pendingConfirmation = helpRequests.filter(item => item.pendingConfirmation && !item.actionTaken).length;
    const completed = helpRequests.filter(item => item.actionTaken).length;

    return { pending, inProgress, pendingConfirmation, completed, total: helpRequests.length };
  };
  
  const counts = getCounts();
  
  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">Trabalhador Precisa de Ajuda</Heading>
        <Tag bg="#4bedffff" color="black" size="lg" borderRadius="full">
          <Flex align="center">
            <Icon as={FiAlertCircle} mr={2} />
            <Text>{helpRequests.length} Solicitações</Text>
          </Flex>
        </Tag>
      </Flex>

      {/* Status Summary */}
      <Flex 
        mb={4} 
        gap={3} 
        flexWrap="wrap"
        bg={filterBg}
        p={3}
        borderRadius="md"
        borderWidth="1px"
        borderColor={filterBorderColor}
      >
        <Button 
          leftIcon={<WarningIcon />} 
          bg="#4bedffff" 
          color="black" 
          size="sm" 
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'active' : 'pending')}
        >
          Aguardando ({counts.pending})
        </Button>
        
        <Button
          leftIcon={<TimeIcon />}
          bg="#4bedffff"
          color="black"
          size="sm"
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'inProgress' ? 'active' : 'inProgress')}
        >
          Em Andamento ({counts.inProgress})
        </Button>

        <Button
          leftIcon={<QuestionIcon />}
          bg="#4bedffff"
          color="black"
          size="sm"
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'pendingConfirmation' ? 'active' : 'pendingConfirmation')}
        >
          Aguardando Confirmação ({counts.pendingConfirmation})
        </Button>

        <Button
          leftIcon={<CheckIcon />}
          bg="#4bedffff"
          color="black"
          size="sm"
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'completed' ? 'active' : 'completed')}
        >
          Concluído ({counts.completed})
        </Button>
        
        <Button 
          leftIcon={<FiRefreshCw />} 
          bg="#4bedffff" 
          color="black" 
          size="sm" 
          variant="solid"
          onClick={resetFilters}
          ml="auto"
        >
          Limpar Filtros
        </Button>
      </Flex>

      {/* Search Box */}
      <Flex mb={4}>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input 
            placeholder="Buscar por usuário, razão..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            borderRadius="md"
          />
        </InputGroup>
      </Flex>

      {loading && !selectedHelpRequest ? (
        <Center h="50vh">
          <Spinner size="xl" thickness="4px" color="purple.500" />
        </Center>
      ) : error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertTitle mr={2}>Erro ao carregar dados</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
          <Button ml="auto" colorScheme="red" size="sm" onClick={fetchHelpRequests}>
            Tentar novamente
          </Button>
        </Alert>
      ) : (
        <Box 
          overflowX="auto"
          borderWidth="1px"
          borderRadius="lg"
          borderColor={tableBorderColor}
        >
          <Table variant="simple">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>ID</Th>
                <Th>Usuário</Th>
                <Th>Razão</Th>
                <Th>Notas Adicionais</Th>
                <Th>Status</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredRequests.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={6}>
                    <Text color="gray.500">
                      {helpRequests.length > 0 
                        ? 'Nenhuma solicitação encontrada com os filtros aplicados' 
                        : 'Nenhuma solicitação de ajuda encontrada'}
                    </Text>
                  </Td>
                </Tr>
              ) : (
                filteredRequests.map((item) => (
                  <Tr 
                    key={item._id} 
                    _hover={{ bg: hoverBg }}
                    transition="background 0.2s"
                  >
                    <Td>
                      <Button
                        variant="link"
                        color="teal"
                        onClick={() => fetchHelpRequestById(item._id)}
                        isTruncated
                        maxWidth="150px"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                      >
                        {item._id.substring(0, 8)}...
                      </Button>
                    </Td>
                    <Td>{item.userName || 'N/A'}</Td>
                    <Td isTruncated maxWidth="200px">{item.reason || 'N/A'}</Td>
                    <Td isTruncated maxWidth="200px">{item.additionalNotes || 'N/A'}</Td>
                    <Td>
                      <StatusBadge
                        isCompleted={item.actionTaken}
                        isInProgress={item.inProgress}
                        isPendingConfirmation={item.pendingConfirmation}
                      />
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button
                          leftIcon={<FiEye />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => fetchHelpRequestById(item._id)}
                        >
                          Detalhes
                        </Button>
                        <Button
                          leftIcon={<FiMessageCircle />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => openAdminChat(item.userId)}
                        >
                          Chat
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Details Modal */}
      {selectedHelpRequest && (
        <WorkerHelpDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          helpData={selectedHelpRequest}
          onActionTaken={handleActionTaken}
          onOpenChat={openAdminChat}
        />
      )}
      
      {/* Chat Modal */}
      <AdminChatModal
        userId={currentUserId}
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
      />
    </Box>
  );
};

export default WorkerNeedHelp;
